import React, { FC } from 'react';
import { Dialog, DialogContent, Button, Grid, DialogTitle, IconButton, Typography, DialogActions } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { JimboResponse, CampaignCoupons } from '@models';
import { useAppDispatch } from '@hooks/redux-custom';
import { useDeleteCouponMutation } from '@services';
import { showToast } from '@store/slices/notification';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  couponToDelete: CampaignCoupons | null;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const DeleteCouponModal: FC<Props> = ({ isOpen, handleClose, couponToDelete, setModal }) => {
  const [deleteCoupon] = useDeleteCouponMutation();

  const dispatch = useAppDispatch();

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: 10 },
      }}
      data-testid="delete-coupons-modal"
    >
      <DialogContent>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <DialogTitle sx={{ fontSize: 30, m: 0, p: 2 }}>
            Delete Coupon
            {handleClose && (
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <Grid item xs={12}>
            <Typography variant="body1">Are you sure you want to delete this voucher?</Typography>
            <Typography variant="body1">Clicking accept will delete the voucher from the customer account.</Typography>
          </Grid>
          <Grid item xs={12}>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button
                sx={{
                  display: 'block',
                  marginRight: '10px',
                  backgroundColor: '#aaaaaa',
                  '&.MuiButtonBase-root:hover': {
                    bgcolor: '#aaaaaa',
                  },
                }}
                variant="contained"
                onClick={handleClose}
                data-testid="delete-coupon-cancel-button"
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                data-testid="accept-delete-coupon-button"
                onClick={() => {
                  deleteCoupon({ campaignId: Number(couponToDelete?.campaignId), couponId: String(couponToDelete?.id) })
                    .unwrap()
                    .then(() => {
                      dispatch(
                        showToast({
                          message: 'Coupon successfully deleted!',
                          type: 'success',
                        }),
                      );
                    })
                    .catch((error: { data: JimboResponse }) => {
                      dispatch(
                        showToast({
                          message: 'Failed to delete coupon!',
                          type: 'error',
                        }),
                      );
                    });

                  setModal(false);
                }}
              >
                Accept
              </LoadingButton>
            </DialogActions>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCouponModal;
